<template>
  <div class="retention-risk-addon">
    <div class="addon-head">
      <div>
        <span>HRM_RETENTION_RISK_INSIGHT</span>
        <h2>离职倾向与稳定性洞察</h2>
        <p>融合员工状态、出勤异常、请假强度、薪资下滑和档案缺口，识别人员稳定性风险。</p>
      </div>
      <el-button type="primary" icon="el-icon-refresh" :loading="loading" @click="loadData">刷新</el-button>
    </div>

    <el-row :gutter="16" class="metric-row">
      <el-col v-for="item in metrics" :key="item.label" :xs="24" :sm="12" :lg="6">
        <div class="metric-card" :class="item.tone">
          <span>{{ item.label }}</span>
          <strong>{{ item.value }}</strong>
          <em>{{ item.meta }}</em>
        </div>
      </el-col>
    </el-row>

    <el-row :gutter="16">
      <el-col :xs="24" :lg="14">
        <el-card shadow="never">
          <div slot="header">高风险员工</div>
          <el-table :data="overview.highRiskStaffList" size="small" v-loading="loading" empty-text="暂无高风险员工">
            <el-table-column prop="staff_name" label="员工" width="110" />
            <el-table-column prop="dept_name" label="部门" min-width="120" />
            <el-table-column prop="risk_level" label="等级" width="80" />
            <el-table-column prop="retention_risk_score" label="风险分" width="90" />
            <el-table-column prop="abnormal_count" label="异常" width="70" />
            <el-table-column prop="leave_days" label="请假天数" width="90" />
            <el-table-column prop="salary_drop_rate" label="薪资下滑%" width="100" />
          </el-table>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="10">
        <el-card shadow="never">
          <div slot="header">风险信号分布</div>
          <div class="signal-list">
            <div v-for="item in overview.riskSignalDistribution" :key="item.signal_name" class="signal-item">
              <span>{{ item.signal_name }}</span>
              <strong>{{ item.signal_count }}</strong>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="16" class="lower-row">
      <el-col :xs="24" :lg="12">
        <el-card shadow="never">
          <div slot="header">部门稳定性排行</div>
          <el-table :data="overview.deptStabilityRanking" size="small" v-loading="loading">
            <el-table-column prop="dept_name" label="部门" min-width="120" />
            <el-table-column prop="staff_count" label="人数" width="70" />
            <el-table-column prop="avg_risk_score" label="平均风险" width="90" />
            <el-table-column prop="high_risk_count" label="高风险" width="80" />
            <el-table-column prop="stability_score" label="稳定分" width="90" />
          </el-table>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="12">
        <el-card shadow="never">
          <div slot="header">薪资下滑观察</div>
          <el-table :data="overview.salaryDropList" size="small" v-loading="loading" empty-text="暂无薪资下滑信号">
            <el-table-column prop="staff_name" label="员工" width="110" />
            <el-table-column prop="dept_name" label="部门" min-width="120" />
            <el-table-column prop="first_salary" label="初始薪资" width="100" />
            <el-table-column prop="last_salary" label="最近薪资" width="100" />
            <el-table-column prop="salary_drop_amount" label="下滑额" width="90" />
          </el-table>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="16" class="lower-row">
      <el-col :xs="24" :lg="12">
        <el-card shadow="never">
          <div slot="header">近期资料变更观察</div>
          <el-table :data="overview.recentUpdateWatchList" size="small" v-loading="loading">
            <el-table-column prop="staff_name" label="员工" width="110" />
            <el-table-column prop="dept_name" label="部门" min-width="120" />
            <el-table-column prop="risk_level" label="等级" width="80" />
            <el-table-column prop="retention_risk_score" label="风险分" width="90" />
            <el-table-column prop="update_time" label="最近更新" min-width="150" />
          </el-table>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="12">
        <el-card shadow="never">
          <div slot="header">月度稳定性趋势</div>
          <div class="trend-list">
            <div v-for="item in overview.monthlyStabilityTrend" :key="item.month_label" class="trend-item">
              <span>{{ item.month_label }}</span>
              <strong>{{ item.stability_score }}</strong>
              <em>异常 {{ item.abnormal_count }} / 请假 {{ item.leave_days }}</em>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { getRetentionRiskInsightOverview } from '../api/retentionRiskInsight'

export default {
  name: 'RetentionRiskInsightOverview',
  data () {
    return {
      loading: false,
      overview: {
        staffCount: 0,
        activeStaffCount: 0,
        inactiveStaffCount: 0,
        highRiskCount: 0,
        mediumRiskCount: 0,
        highRiskStaffList: [],
        deptStabilityRanking: [],
        riskSignalDistribution: [],
        salaryDropList: [],
        recentUpdateWatchList: [],
        monthlyStabilityTrend: []
      }
    }
  },
  computed: {
    metrics () {
      return [
        { label: '员工总数', value: this.overview.staffCount, meta: `${this.overview.activeStaffCount} 个在职` },
        { label: '高风险', value: this.overview.highRiskCount, meta: '建议管理复核', tone: 'red' },
        { label: '中风险', value: this.overview.mediumRiskCount, meta: '持续观察', tone: 'orange' },
        { label: '异常状态', value: this.overview.inactiveStaffCount, meta: '状态非正常', tone: 'blue' }
      ]
    }
  },
  mounted () {
    this.loadData()
  },
  methods: {
    loadData () {
      this.loading = true
      getRetentionRiskInsightOverview().then(response => {
        if (response.code === 200) {
          this.overview = Object.assign({}, this.overview, response.data || {})
        } else {
          this.$message.warning(response.message || '离职倾向与稳定性洞察加载失败')
        }
      }).finally(() => {
        this.loading = false
      })
    }
  }
}
</script>

<style lang="less" scoped>
.retention-risk-addon {
  padding: 20px;
  background: #f5f7fb;
  min-height: 100%;
}

.addon-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
  padding: 22px 24px;
  background: #ffffff;
  border: 1px solid #e6ebf2;
  border-radius: 6px;
}

.addon-head span {
  display: block;
  margin-bottom: 8px;
  color: #075985;
  font-size: 12px;
  font-weight: 700;
}

.addon-head h2 {
  margin: 0 0 8px;
  color: #17233d;
  font-size: 24px;
}

.addon-head p {
  margin: 0;
  color: #5f6f89;
}

.metric-row {
  margin-bottom: 16px;
}

.metric-card {
  min-height: 118px;
  padding: 18px;
  margin-bottom: 16px;
  background: #ffffff;
  border: 1px solid #e6ebf2;
  border-radius: 6px;
}

.metric-card span,
.metric-card em {
  display: block;
  color: #6b7a90;
  font-style: normal;
}

.metric-card strong {
  display: block;
  margin: 10px 0 8px;
  color: #17233d;
  font-size: 30px;
  line-height: 1;
}

.metric-card.red strong {
  color: #d93026;
}

.metric-card.orange strong {
  color: #d9822b;
}

.metric-card.blue strong {
  color: #2f6fec;
}

.lower-row {
  margin-top: 16px;
}

.signal-list,
.trend-list {
  display: grid;
  gap: 10px;
}

.trend-list {
  max-height: 360px;
  overflow: auto;
}

.signal-item,
.trend-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  background: #f8fafc;
  border: 1px solid #edf1f7;
  border-radius: 6px;
}

.trend-item {
  align-items: flex-start;
  flex-direction: column;
}

.signal-item span,
.trend-item span,
.trend-item em {
  color: #5f6f89;
  font-style: normal;
}

.signal-item strong,
.trend-item strong {
  color: #17233d;
  font-size: 16px;
}
</style>
